import React from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_LANGUAGE, applyLanguage, hasLanguageChoice, offeredLanguages } from '../../i18n';
import { languageCode, languageEndonym, storePreAccountLanguage } from '../../lib/language';

/**
 * The small language switch in the header of the pages shown before login
 * (landing, signup, login, password reset): one short code per offered language,
 * "PT | ES | EN" with all three. The choice is kept on the device until there is
 * an account to hold it (lib/language.ts). Renders nothing while English is the
 * only language offered, and shows only the languages that are.
 */
export const LanguageSwitch: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { t, i18n } = useTranslation();
  if (!hasLanguageChoice()) return null;

  // English last: "PT | EN", "PT | ES | EN".
  const languages = [
    ...offeredLanguages().filter((lang) => lang !== DEFAULT_LANGUAGE),
    DEFAULT_LANGUAGE,
  ];

  return (
    <div role="group" aria-label={t('language.switchLabel')} className={`flex items-center gap-1 text-sm ${className}`}>
      {languages.map((lang, index) => {
        const current = i18n.language === lang;
        return (
          <React.Fragment key={lang}>
            {index > 0 && <span aria-hidden="true" className="text-gray-500">|</span>}
            <button
              type="button"
              lang={lang}
              aria-label={languageEndonym(lang)}
              aria-pressed={current}
              onClick={() => {
                storePreAccountLanguage(lang);
                applyLanguage(lang);
              }}
              className={`px-1.5 py-1 rounded min-h-[32px] transition-colors ${
                current ? 'font-semibold text-charcoal' : 'text-mid hover:text-charcoal'
              }`}
            >
              {languageCode(lang)}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
};
